import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ConversionMetric } from "@/api/analytics";

interface ConversionMetricsProps {
  metrics?: ConversionMetric[];
  loading?: boolean;
}

/** Right column of the funnel section — small list of derived
 *  metrics. Each row: label + big number + unit suffix + optional
 *  delta vs the previous comparable window. */
export function ConversionMetrics({
  metrics,
  loading,
}: ConversionMetricsProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-lg bg-slate-100"
          />
        ))}
      </div>
    );
  }
  const safe = metrics ?? [];
  if (safe.length === 0) {
    return (
      <p className="text-sm italic text-slate-400">
        Not enough data for conversion metrics yet.
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {safe.map((m, idx) => {
        const value =
          m.unit === "%"
            ? `${(Math.round(m.value * 10) / 10).toFixed(1)}%`
            : `${(Math.round(m.value * 10) / 10).toFixed(1)} ${m.unit}`;
        return (
          <li
            key={m.key ?? `metric-${idx}`}
            className="flex items-baseline justify-between gap-3 border-b border-slate-900/[0.06] pb-2 last:border-b-0 last:pb-0"
          >
            <span className="text-xs font-medium text-slate-500">
              {m.label}
            </span>
            <span className="flex items-baseline gap-2">
              <span className="text-base font-semibold tabular-nums text-slate-900">
                {value}
              </span>
              {m.delta_pct != null && Number.isFinite(m.delta_pct) && (
                <Delta delta={m.delta_pct} />
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Delta({ delta }: { delta: number }) {
  const rounded = Math.round(delta * 10) / 10;
  const positive = rounded > 0;
  const negative = rounded < 0;
  const Icon = positive ? ArrowUp : negative ? ArrowDown : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums",
        positive && "text-emerald-600",
        negative && "text-rose-600",
        !positive && !negative && "text-slate-400",
      )}
    >
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {Math.abs(rounded)}%
    </span>
  );
}
